import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    putCongrats,
    putNextScreenParams,
} from "@data/redux/actions/navigation"
import { useNavigation, useRoute } from "@react-navigation/native"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { connect } from "react-redux"
import { DialogM } from "../../../common/components/alert/dialog/Dialog"
import { ModalM } from "../../../common/components/modal/ModalM"
import { useApiStatus } from "../../../common/hooks/useApiStatus"
import { useCurrentActivity } from "../../../common/hooks/useCurrentActivity"
import useLiveChatUserId from "../../../common/hooks/useLiveChatUserId"
import useNextTodoActivity from "../../../common/hooks/useNextTodoActivity"
import { usePrerequisiteQuiz } from "../../../common/hooks/usePrerequisiteQuiz"
import LiveChat from "../../../common/liveMode/LiveChat"
import SamCheckpointOverlay from "../../../common/liveMode/SamCheckpointOverlay"
import { showActivityCongrats } from "./ActivityFinalizedHelpers"
import CorrectionSequence from "./CorrectionSequence"
import AutoEvalOverlay from "../../../common/liveMode/autoMode/AutoEvalOverlay"
import AutoModeChoiceModal from "../../../common/liveMode/autoMode/AutoModeChoiceModal"
import useAutoModeAutoClose from "../../../common/hooks/useAutoModeAutoClose"
import { theme } from "../../../common/theme/theme"
import { DimensionsContext } from "../../../common/utils/mobileUtils"
import {
    clearCheckpointConversation,
    setCheckpointConversation,
    setFocusedMode,
    setMiniMode,
    setNewTodoActivity,
} from "../../../data/redux/actions/liveMode"
import { authService } from "../../../data/services/authService"
import { liveService } from "../../../data/services/liveService"
import {
    getActivityStatus,
    getMostRecentActivity,
} from "../../../data/utility/dataUtils"
import ActivityFinishedScreen from "../read/ActivityFinishedScreen"
import ActivityDeletedModal from "./ActivityDeletedModal"
import ActivityScreen from "./ActivityScreen"
import ActivityWaitingScreen from "./ActivityWaitingScreen"

function getActivityProgressStatus(activity) {
    if (activity?.progressStatus) {
        return activity.progressStatus
    }
    return "loading"
}

function getActivityComponent(activityProgressStatus, props, quizGateProps) {
    switch (activityProgressStatus) {
        case "ongoing":
            return <ActivityScreen {...props} quizGateProps={quizGateProps} />
        case "finished":
            return <ActivityFinishedScreen {...props} />
        case "loading":
            return <ActivityWaitingScreen {...props} loading />
        case "none":
            return <ActivityWaitingScreen {...props} />
        default:
            return null
    }
}

function ActivityWrapperScreen(props) {
    const navigation = props?.navigation ?? useNavigation()
    const route = props?.route ?? useRoute()

    const { setCurrentActivity } = useCurrentActivity()
    const { selectNextActivity } = useNextTodoActivity()
    const [showDeletedModal, setShowDeletedModal] = useState(false)
    // La copie que Sam corrige, le temps de l'attente et de la correction.
    const [correctionSequence, setCorrectionSequence] = useState(null)
    const countdownRef = useRef(null)

    const activityProgressStatus = getActivityProgressStatus(
        props.currentActivity
    )
    const activity = props.currentActivity?.activityData

    // Use the consolidated quiz hook
    const {
        hasQuizOverlay,
        quizGateProps,
        checkpointConversation,
        handleCloseQuiz,
    } = usePrerequisiteQuiz(activity)

    const dimensions = useContext(DimensionsContext)
    const { liveChatUserId, setLiveChatUserId } = useLiveChatUserId(
        navigation,
        route
    )

    // Handle exit dialog
    const onExit = useCallback(
        (hasMedia = false, handleShowMyWorkModal = () => {}) => {
            const title = hasMedia
                ? "Avant de quitter, ton travail sera soumis"
                : "Veux-tu quitter ?"
            const description = hasMedia
                ? "Et c'est là que tu recevras tes XP !"
                : "Les chronomètres seront sauvegardés, et tu pourras reprendre quand tu veux"
            const buttonText1 = hasMedia ? "Soumettre et quitter" : "Quitter"

            DialogM.show({
                text1: title,
                text2: description,
                champion: "abou",
                buttonColor1: hasMedia
                    ? theme.colors.success
                    : theme.colors.primary,
                buttonText1: buttonText1,
                onPress: () => {
                    if (hasMedia) {
                        handleShowMyWorkModal()
                    } else {
                        setCurrentActivity(null)
                        // Prévenir l'appelant qui embarque ce wrapper (session
                        // Sam) : sans ça il ne peut que DÉDUIRE la sortie du
                        // démontage, et il restait sur une roue infinie le
                        // temps de s'en apercevoir.
                        props.onExit?.()
                    }
                },
                buttonText2: "Retour",
            })
        },
        [setCurrentActivity, props.onExit]
    )

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setCurrentActivity(null)
        }
    }, [])

    // Handle new todo activity from tutor
    useEffect(() => {
        if (!props.newTodoActivity || !props.forceActivityFocus) {
            props.setNewTodoActivity(null)
            return
        }

        const checkpointConversationId =
            props.checkpointConversation?.conversation?.id
        const isOngoing =
            props.checkpointConversation?.conversation?.status === "ongoing"

        if (checkpointConversationId) {
            if (isOngoing) {
                props.apiRequest(
                    liveService.finishConversation,
                    { conversation_id: checkpointConversationId },
                    {
                        interruption_reason: "tutor_interruption",
                        ended_at: new Date().toISOString(),
                    }
                )
            }
            props.clearCheckpointConversation()
        }

        setCurrentActivity({
            activityData: props.newTodoActivity,
            progressStatus:
                props.newTodoActivity?.status === "todo"
                    ? "ongoing"
                    : "finished",
        })

        PlaySoundM.play(require("@assets/sounds/falling.mp3"))
        SnackBarM.show({
            text1: "🎯 Nouvelle activité affichée",
            duration: 4000,
        })

        props.setMiniMode(true)
        props.setFocusedMode(false)
        props.setNewTodoActivity(null)
    }, [
        props.newTodoActivity,
        props.forceActivityFocus,
        activityProgressStatus,
        setCurrentActivity,
    ])

    // Handle deleted activity modal
    useEffect(() => {
        setShowDeletedModal(
            Boolean(props.currentActivity?.activityData?.isDeleted)
        )
    }, [props.currentActivity?.activityData?.isDeleted])

    // Handle continue after activity deleted
    const handleContinue = useCallback(() => {
        setShowDeletedModal(false)
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
        }

        selectNextActivity(
            props.user?.todo_activities.filter((todo) => !todo.isDeleted),
            props.currentActivity?.activityData?.id
        )
    }, [props.user, props.currentActivity, selectNextActivity])

    // Initial activity load on user data fetch
    useApiStatus(authService.getUser, null, true, () => {
        if (props.currentActivity) {
            return
        }

        const todoActivities =
            props.user?.todo_activities?.filter((todo) => !todo.isDeleted) || []
        let activityToLaunch = null

        const suggestedId = props.suggestedActivities?.[props.liveSessionId]
        if (suggestedId) {
            activityToLaunch = todoActivities.find((a) => a.id === suggestedId)
        }

        if (!activityToLaunch) {
            const activities =
                props.user?.todo_activities?.concat(props.user?.activities) ||
                []
            activityToLaunch = getMostRecentActivity(activities, null)
        }

        setCurrentActivity({
            activityData: activityToLaunch,
            progressStatus: getActivityStatus(activityToLaunch),
        })
    })

    // Auto-close the auto-eval/choice overlays if the live session ends or if
    // the tutor pushes an activity while the overlay is up. The overlay itself
    // is opened later, from CongratsScreen.proceedWithNavigation, so it shows
    // *after* the activity-finalized congrats screen plays.
    useAutoModeAutoClose()

    // La séquence tient à l'activité dont elle corrige la copie. Dès que
    // l'élève est ailleurs — une activité affichée par le tuteur, ouverte
    // depuis le chat, la suivante — elle s'efface ; la correction l'attend
    // dans son activité.
    useEffect(() => {
        if (correctionSequence && correctionSequence.activity?.id !== activity?.id) {
            setCorrectionSequence(null)
        }
    }, [activity?.id])

    // L'écran de fin d'une copie corrigée se prépare quand l'élève y arrive :
    // les activités à faire sont celles de maintenant, pas celles de l'envoi.
    const showCongratsAfterCorrection = () => {
        const { congrats } = correctionSequence
        setCorrectionSequence(null)
        showActivityCongrats({ ...props, navigation }, congrats)
    }

    const data = {
        navigation,
        route,
        onExit,
        hasQuizOverlay,
        // Un appelant qui affiche son propre écran de fin (la session Sam) le
        // reçoit à la place du congrats standard, après la correction quand
        // il y en a une.
        onCompletionScreen: props.onCompletionScreen,
        onCorrectionExpected: setCorrectionSequence,
        correctionInFlow: correctionSequence !== null,
        ...props.currentActivity,
    }

    return (
        <>
            {getActivityComponent(activityProgressStatus, data, quizGateProps)}

            {showDeletedModal && (
                <ActivityDeletedModal
                    visible={showDeletedModal}
                    onContinue={handleContinue}
                />
            )}

            <ModalM
                visible={liveChatUserId !== null}
                onDismiss={() => setLiveChatUserId(null)}
                style={{
                    width: dimensions?.width,
                    height: "90%",
                    overflow: "hidden",
                }}
            >
                <LiveChat
                    userId={liveChatUserId}
                    onExit={() => setLiveChatUserId(null)}
                    nav={navigation}
                    route={route}
                />
            </ModalM>

            {/* Avant le checkpoint de Sam : une main levée pendant l'attente
                ouvre sa conversation par-dessus, pas dessous. */}
            {correctionSequence && (
                <CorrectionSequence
                    key={correctionSequence.activity?.id}
                    activity={correctionSequence.activity}
                    correctionWait={correctionSequence.correctionWait}
                    onDone={showCongratsAfterCorrection}
                />
            )}

            <SamCheckpointOverlay
                key={
                    checkpointConversation?.conversation?.id || "no-checkpoint"
                }
                visible={checkpointConversation !== null}
                conversation={checkpointConversation}
                onClose={handleCloseQuiz}
            />

            <AutoEvalOverlay />
            <AutoModeChoiceModal />
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        liveSessionId: state.liveMode?.liveSessionId,
        isLive: state.liveMode.isLiveModeEnabled,
        suggestedActivities: state.liveMode?.suggestedActivities,
        forceActivityFocus: state.liveMode?.forceActivityFocus,
        newTodoActivity: state.liveMode?.newTodoActivity,
        currentActivity: state.data?.currentActivity?.activity,
        checkpointConversation: state.liveMode?.checkpointConversation,
        quizProgress: state.liveMode?.quizProgress,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putNextScreenParams,
    putCongrats,
    setNewTodoActivity,
    setMiniMode,
    setFocusedMode,
    clearCheckpointConversation,
    setCheckpointConversation,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ActivityWrapperScreen)
